import * as yup from "yup";
import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useFormik} from "formik";
import {Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import {IPermissionGroupById, IPermissionGroupForm} from "../../../store/permissions/types";
import {PermissionsService} from "../../../services/permissions.service";
import Select from "../../../components/Common/Select";
import {components} from "react-select";
import permissionGroupIcons from "./PermissionGroupIcons.json";

interface ModalPermissionGroupProps {
    isOpen: boolean;
    groupId: number | null;
    onClose: () => void;
}

const groupsIconsSelect = permissionGroupIcons.map(icon => {
    const prefixRemoved = icon.replace(/^bx-/, "");
    return {
        label: prefixRemoved.split("-")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        value: prefixRemoved,
    }
});

const SingleValue = ({children, ...props}: any) => (
    <components.SingleValue className="d-flex align-items-center gap-2" {...props}>
        <i className={`bx bx-${props.data.value} fs-5`}/>
        {children}
    </components.SingleValue>
);

const Option = (props: any) => (
    <components.Option className="d-flex align-items-center gap-3" {...props}>
        <i className={`bx bx-${props.value} fs-5`}/>
        {props.children}
    </components.Option>
);

const ModalPermissionGroup: React.FC<ModalPermissionGroupProps> = ({isOpen, groupId, onClose}) => {
    const isEditModal = groupId !== null;
    const queryClient = useQueryClient();

    const [savedGroup, setSavedGroup] = useState<boolean>(false);

    const {
        data: group,
        isFetching
    } = useQuery({
        queryKey: ["keyPermissionsGroup", groupId],
        queryFn: () => PermissionsService.getGroupById(groupId || -1),
        select: (g: IPermissionGroupById) => g.data,
        enabled: isEditModal,
    });

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyPermissionsGroups"]})
            .then(() => onClose())
    }

    const {mutate: createGroup, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IPermissionGroupForm) => PermissionsService.createGroup(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreatePermissionsGroup"],
    });

    const {mutate: updateGroup, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IPermissionGroupForm) => PermissionsService.updateGroup(groupId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdatePermissionsGroup", groupId],
    });

    const validationSchema: yup.Schema<IPermissionGroupForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("Group name is required"),

        icon: yup.string()
            .required("Icon is required"),

        permissions: yup.array()
            .required("Permissions are required")
    });

    const initialValues: IPermissionGroupForm = {
        name: group?.name || "",
        icon: group?.icon || "",
        permissions: group?.permissions.map(p => p.id) || []
    }

    const handleSubmitForm = (result: IPermissionGroupForm) => {
        setSavedGroup(true);

        if (!isEditModal) createGroup(result);
        else updateGroup(result);
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const closeModal = () => {
        onClose();
        resetForm();
    };

    const handleChangeIcon = (select: any) => {
        setFieldValue("icon", select.value)
    }

    const selectedIcon = groupsIconsSelect?.find((icon) => icon.value === values.icon)

    const isPendingSave = isPendingCreate || isPendingUpdate || (!!queryClient.isFetching() && savedGroup);

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} Group
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={handleSubmit} className="needs-validation">
                    <ModalBody>
                        <div className="mb-3">
                            <Label htmlFor="name" className="mb-1 ms-1">Group Name*</Label>
                            <Input
                                placeholder="Enter group name..."
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>
                        <div>
                            <Label htmlFor="icon" className="mb-1 ms-1">Icon*</Label>
                            <Select
                                isSearchable
                                name="icon"
                                label="Icon"
                                placeholder="Select an icon"
                                components={{SingleValue, Option}}
                                options={groupsIconsSelect}
                                onChange={handleChangeIcon}
                                value={selectedIcon}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </form>
            )}
        </Modal>
    )
}

export default ModalPermissionGroup;